/**
 * Уведомления.
 *
 * CLAUDE.md: журнал доставки — часть доказательной базы, а не техническая
 * деталь. «Уведомление о критическом дефекте доставлено ответственному в 06:42,
 * открыто в 06:51» — это то, что закрывает правило 16: приложение не выносит
 * вердикт «эксплуатация запрещена», оно фиксирует факт и уведомляет.
 *
 * Второй сквозной мотив — усталость от алертов. Категория умирает не от багов,
 * а от того, что на второй неделе менеджер глушит уведомления. Отсюда уровни,
 * дедупликация, тихие часы и обязательное действие в каждом уведомлении.
 */
import { z } from 'zod';
import { objectIdSchema } from './common.js';
export const NOTIFICATION_EVENTS = [
    'defect.critical',
    'defect.reported',
    'defect.closed',
    'inspection.missed',
    'inspection.submitted',
    'trust.low',
];
/**
 * Уровни. Критичное идёт немедленно во все каналы и эскалируется;
 * важное собирается в дайджест раз в смену; информационное живёт только
 * в интерфейсе и никогда никого не будит.
 */
export const SEVERITIES = ['critical', 'important', 'info'];
export const EVENT_SEVERITY = {
    'defect.critical': 'critical',
    'inspection.missed': 'important',
    'defect.reported': 'important',
    'trust.low': 'important',
    'defect.closed': 'info',
    'inspection.submitted': 'info',
};
/** Право, дающее право получать уведомление этого типа. */
export const EVENT_AUDIENCE = {
    'defect.critical': 'defect:read',
    'defect.reported': 'defect:read',
    'defect.closed': 'defect:read',
    'inspection.missed': 'inspection:read',
    'inspection.submitted': 'inspection:read',
    'trust.low': 'trust:read',
};
export const CHANNELS = ['in_app', 'push', 'email', 'sms'];
/** Какие каналы задействуются по умолчанию для уровня. */
export const DEFAULT_CHANNELS = {
    critical: ['in_app', 'push', 'email', 'sms'],
    important: ['in_app', 'push'],
    info: ['in_app'],
};
/**
 * Каждое уведомление несёт действие. Уведомление, из которого нельзя ничего
 * сделать, — это шум, и его отключают.
 */
export const NOTIFICATION_ACTIONS = [
    'open_defect',
    'open_inspection',
    'explain_miss',
    'open_unit',
];
export const DELIVERY_STATUSES = ['pending', 'sent', 'failed', 'skipped'];
/* --------------------------- эскалация ----------------------------------- */
/**
 * Лестница эскалации критичного уведомления.
 * Не открыто за 15 минут — следующий ответственный, за 30 — владелец.
 */
export const ESCALATION_LADDER = [
    { afterMinutes: 15, audience: 'fleet_admin' },
    { afterMinutes: 30, audience: 'owner' },
];
export function escalationDelayMs(level) {
    const step = ESCALATION_LADDER[level];
    return step === undefined ? null : step.afterMinutes * 60_000;
}
/* ---------------------------- тихие часы --------------------------------- */
export const quietHoursSchema = z
    .object({
    /** Местное время начала и конца, HH:MM. */
    from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
})
    .nullable();
function minutesOfDay(time) {
    const [h, m] = time.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
}
/**
 * Действуют ли тихие часы в этот момент.
 * Интервал может пересекать полночь: 22:00–06:00 — нормальный случай.
 */
export function inQuietHours(quiet, localMinutes) {
    if (quiet === null)
        return false;
    const from = minutesOfDay(quiet.from);
    const to = minutesOfDay(quiet.to);
    return from <= to
        ? localMinutes >= from && localMinutes < to
        : localMinutes >= from || localMinutes < to;
}
/**
 * Тихие часы настраиваются по связке роль × уровень, а не глобально:
 * водитель работает в четыре утра, менеджер спит. Критичное их не соблюдает —
 * иначе смысл уровня теряется.
 */
export function respectsQuietHours(severity) {
    return severity !== 'critical';
}
/* ----------------------------- схемы API --------------------------------- */
export const pushSubscriptionSchema = z.object({
    endpoint: z.url().max(1024),
    keys: z.object({
        p256dh: z.string().min(1).max(256),
        auth: z.string().min(1).max(256),
    }),
    deviceId: z.string().min(8).max(128).nullable().default(null),
});
export const notificationPrefSchema = z.object({
    event: z.enum(NOTIFICATION_EVENTS),
    channels: z.array(z.enum(CHANNELS)),
    quietHours: quietHoursSchema.default(null),
});
export const markReadSchema = z.object({
    notificationIds: z.array(objectIdSchema).min(1).max(200),
});
export const notificationSchema = z.object({
    id: objectIdSchema,
    event: z.enum(NOTIFICATION_EVENTS),
    severity: z.enum(SEVERITIES),
    title: z.string(),
    body: z.string(),
    action: z.enum(NOTIFICATION_ACTIONS),
    target: z.object({ kind: z.string(), id: z.string() }),
    unitNumber: z.string().nullable(),
    /** Сколько однотипных событий свернулось в это уведомление. */
    occurrences: z.number().int().min(1),
    createdAt: z.coerce.date(),
    readAt: z.coerce.date().nullable(),
    escalationLevel: z.number().int().min(0),
});
/**
 * Ключ дедупликации. Одно колесо ниже порога три дня подряд — один тред
 * с обновлениями, а не три отдельных уведомления.
 */
export function dedupeKey(params) {
    return [params.event, params.unitId, params.itemId ?? '-'].join('|');
}
/**
 * Полезная нагрузка push. Только тип и идентификатор: push проходит
 * через серверы Google и Apple, и содержимое дефектов туда попадать не должно.
 * Детали клиент забирает через API.
 */
export const pushPayloadSchema = z.object({
    type: z.literal('notification'),
    id: objectIdSchema,
    severity: z.enum(SEVERITIES),
});
//# sourceMappingURL=notify.js.map